import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';

const PublicHeader = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    else navigate('/#contact');
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/70 backdrop-saturate-150 border-b border-border/50 shadow-sm'
          : 'bg-transparent'
      }`}
      data-testid="public-header"
    >
      <div className="container-grid flex items-center justify-between h-18 py-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center transition-colors duration-300 ${scrolled ? 'bg-[#042F2E]' : 'bg-white/10'}`}>
            <span className="text-white font-bold text-lg font-heading">G</span>
          </div>
          <span className={`text-xl font-semibold tracking-tight font-heading transition-colors duration-300 ${scrolled ? 'text-[#042F2E]' : 'text-white'}`}>Growartha</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {[
            { label: 'Services', id: 'services' },
            { label: 'Platform', id: 'platform' },
            { label: 'Pricing', id: 'pricing' },
            { label: 'Team', id: 'team' },
          ].map((item) => (
            <a key={item.id} href={`#${item.id}`}
              onClick={(e) => { e.preventDefault(); const el = document.getElementById(item.id); if (el) el.scrollIntoView({ behavior: 'smooth' }); else navigate(`/#${item.id}`); }}
              className={`text-sm font-medium transition-colors duration-200 cursor-pointer ${scrolled ? 'text-foreground/70 hover:text-foreground' : 'text-white/70 hover:text-white'}`}>
              {item.label}
            </a>
          ))}
          {user ? (
            <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard" className="bg-[#042F2E] hover:bg-[#042F2E]/90 text-white rounded-full px-6">
              Dashboard
            </Button>
          ) : (
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/login')} data-testid="login-button" className={`text-sm font-medium rounded-full px-6 ${scrolled ? 'border-[#042F2E] text-[#042F2E] hover:bg-[#042F2E] hover:text-white' : 'border-white/60 text-white hover:bg-white/10 hover:border-white'}`}>
                Log in
              </Button>
              <Button onClick={scrollToContact} data-testid="header-contact-cta" className="bg-[#14B8A6] hover:bg-[#0fa093] text-[#042F2E] rounded-full px-6 font-semibold">
                Book Consultation
              </Button>
            </div>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button className={`md:hidden p-2 rounded-lg transition-colors ${scrolled ? 'hover:bg-muted' : 'hover:bg-white/10 text-white'}`} onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="md:hidden border-t bg-white/95 backdrop-blur-xl p-5 space-y-1" data-testid="mobile-nav">
          {[
            { label: 'Services', id: 'services' },
            { label: 'Platform', id: 'platform' },
            { label: 'Pricing', id: 'pricing' },
            { label: 'Team', id: 'team' },
            { label: 'FAQ', id: 'faq' },
          ].map((item) => (
            <a key={item.id} href={`#${item.id}`} className="block text-sm font-medium py-3 px-3 rounded-lg hover:bg-muted transition-colors cursor-pointer"
              onClick={(e) => { e.preventDefault(); const el = document.getElementById(item.id); if (el) el.scrollIntoView({ behavior: 'smooth' }); else navigate(`/#${item.id}`); setMobileOpen(false); }}>
              {item.label}
            </a>
          ))}
          <div className="pt-3 space-y-2">
            {user ? (
              <Button className="w-full bg-[#042F2E] rounded-full" onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}>Dashboard</Button>
            ) : (
              <>
                <Button variant="ghost" className="w-full" onClick={() => { navigate('/login'); setMobileOpen(false); }}>Log in</Button>
                <Button className="w-full bg-[#14B8A6] hover:bg-[#0fa093] text-[#042F2E] font-semibold rounded-full" onClick={scrollToContact}>Book Consultation</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
